import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, ProviderService, UiStateService } from '@app/core/services';
import { ApplicationInsightsService, AppInsightTags } from '@app/core/services/application-insights.service';
import { ConfigService } from '@app/core/services/config.service';
import { MerakiAuthService } from '@app/core/services/meraki-auth.service';
import { SidebarLocations } from '@app/core/services/state/ui-state/ui-state.store';
import { WINDOW } from '@app/core/services/window.provider';
import { BehaviorSubject, combineLatest, EMPTY, fromEvent, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import * as uuidV4 from 'uuid/v4';
import {
  PracticeAgentAnalyticsDetails,
  PracticeAgentAnalyticsEventType,
  PracticeAgentHomeTab,
  PracticeAgentSessionEndReason,
} from './practice-agent-analytics.models';
import { PracticeAgentAnalyticsService } from './practice-agent-analytics.service';
import { PracticeAgentWidgetState, PracticeAgentWidgetStateService } from './practice-agent-widget-state.service';
import { PracticeAgentWidgetControlService } from './practice-agent-widget-control.service';
import {
  HbcAuthChangedMessage,
  HbcContextChangedMessage,
  HbcInitMessage,
  HbcPracticeAgentMessage,
  HbcSuppressionChangedMessage,
  isPaSizeChangedMessage,
  isPaVisibilityChangedMessage,
  PaSizeChangedMessage,
  PaVisibilityChangedMessage,
  PRACTICE_AGENT_MESSAGE_VERSION,
  PracticeAgentContext,
  PracticeAgentContextPayload,
  PracticeAgentPage,
  PracticeAgentPresentationMode,
  PracticeAgentSuppression,
  PracticeAgentSuppressionMode,
} from './practice-agent-message.models';

const AUTH_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
const SNOOZE_DURATION_MS = 60 * 60 * 1000;
const SUPPRESSION_RETRY_INTERVAL_MS = 60 * 1000;
const CONTEXT_RETRY_INTERVAL_MS = 60 * 1000;
const PRESENTATION_LEASE_RENEWAL_INTERVAL_MS = 2 * 60 * 1000;
const PRESENTATION_SESSION_STORAGE_KEY = 'practiceAgent.presentationSessionId.v1';
const TIMED_RETRIGGER_HOUR_SAST = 16;
const SAST_UTC_OFFSET_MS = 2 * 60 * 60 * 1000;
const TIMED_RETRIGGER_SNOOZE_WINDOW_MS = 60 * 60 * 1000;

type ContextMessageType = 'hbc.init' | 'hbc.context.changed';
type PresentationTrigger = 'home-landing' | 'home-after-16' | 'pathology-centre' | 'snooze-expiry' | 'manual-open';

interface ContextMessageRequest {
  type: ContextMessageType;
  context: PracticeAgentContext;
  sendSuppressionEnded: boolean;
  contextId: number;
  presentationMode: PracticeAgentPresentationMode;
}

@Injectable()
export class PracticeAgentBridgeService implements OnDestroy {
  readonly widgetUrl = this.configService.config.practiceAgent.widgetUrl || '';

  private readonly widgetOrigin = this.getWidgetOrigin(this.widgetUrl);
  private readonly snoozeDurationMs = this.getSnoozeDurationMs();
  readonly snoozeDurationLabel = this.getSnoozeDurationLabel();
  private readonly paRequestedVisibilitySubject = new BehaviorSubject(false);
  private readonly homePageActiveSubject = new BehaviorSubject(false);
  private readonly suppressedSubject = new BehaviorSubject(false);
  private readonly expandedSubject = new BehaviorSubject(false);
  private readonly contextMessageRequests = new Subject<ContextMessageRequest>();
  private readonly subscriptions = new Subscription();
  private readonly sessionId = this.getPresentationSessionId();

  private iframe: HTMLIFrameElement;
  private latestContext: PracticeAgentContext;
  private authRefreshSubscription: Subscription;
  private suppressionTimerSubscription: Subscription;
  private contextRetrySubscription: Subscription;
  private timedRetriggerSubscription: Subscription;
  private presentationLeaseSubscription: Subscription;
  private suppressionMode: PracticeAgentSuppressionMode;
  private suppressionUntil: string;
  private suppressionEndPending = false;
  private currentContextId = 0;
  private nextContextId = 0;
  private enabled = false;
  private iframeLoaded = false;
  private initialContextSent = false;
  private started = false;
  private currentPage: PracticeAgentPage;
  private currentHomeTab: PracticeAgentHomeTab = 'other';
  private presentationTrigger: PresentationTrigger;
  private restoreVisibleAfterRefresh = false;
  private presentationClaimPending = false;

  private readonly firebaseAuthenticated$ = this.merakiAuthService.authState$.pipe(
    map(user => !!user),
    startWith(false),
    distinctUntilChanged()
  );

  private readonly widgetEnabled$ = this.providerService.provider$.pipe(
    map(provider => ({
      practiceTenantId: provider?.PracticeTenantId || '',
      practiceId: provider?.PracticeId || '',
    })),
    distinctUntilChanged(
      (previous, current) => previous.practiceTenantId === current.practiceTenantId && previous.practiceId === current.practiceId
    ),
    switchMap(() =>
      this.providerService.practiceAgentWidgetEnabled$.pipe(
        startWith(false),
        catchError(() => of(false))
      )
    )
  );

  private readonly providerPresent$ = this.providerService.provider$.pipe(
    map(provider => !!provider?.PracticeNumber && !!provider?.PracticeId),
    distinctUntilChanged()
  );

  readonly enabled$ = this.widgetOrigin
    ? combineLatest([this.authService.authenticated$, this.firebaseAuthenticated$, this.providerPresent$, this.widgetEnabled$]).pipe(
        map(
          ([authenticated, firebaseAuthenticated, providerPresent, widgetEnabled]) =>
            authenticated && firebaseAuthenticated && providerPresent && widgetEnabled
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    : of(false);

  readonly visible$ = combineLatest([
    this.paRequestedVisibilitySubject,
    this.homePageActiveSubject,
    this.suppressedSubject,
    this.enabled$,
  ]).pipe(
    map(
      ([paRequestedVisibility, homePageActive, suppressed, enabled]) => paRequestedVisibility && homePageActive && !suppressed && enabled
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly expanded$ = this.expandedSubject.asObservable();

  readonly sidebarOpen$ = this.uiStateService.sidebar$.pipe(
    map(sidebar => sidebar !== SidebarLocations.HIDDEN),
    distinctUntilChanged()
  );

  readonly wideSidebarOpen$ = this.uiStateService.sidebar$.pipe(
    map(sidebar => sidebar === SidebarLocations.CALENDAR),
    distinctUntilChanged()
  );

  private readonly currentPage$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.urlAfterRedirects || event.url),
    startWith(''),
    map(url => url || this.router.url),
    tap(url => this.handleNavigation(url)),
    map(url => this.getCurrentPage(url)),
    distinctUntilChanged()
  );

  private readonly context$ = combineLatest([
    this.providerService.provider$,
    this.uiStateService.currentPatientId$,
    this.uiStateService.activePatients$,
    this.currentPage$,
  ]).pipe(
    map(([provider, currentPatientId, activePatients, currentPage]) => ({
      practiceNumber: provider?.PracticeNumber || '',
      doctorId: provider?.PracticeId || '',
      patientId: this.getContextPatientId(currentPage, currentPatientId, activePatients),
      currentPage,
    })),
    filter(context => !!context.practiceNumber && !!context.doctorId),
    distinctUntilChanged(
      (previous, current) =>
        previous.practiceNumber === current.practiceNumber &&
        previous.doctorId === current.doctorId &&
        previous.patientId === current.patientId &&
        previous.currentPage === current.currentPage
    )
  );

  constructor(
    private authService: AuthService,
    private providerService: ProviderService,
    private uiStateService: UiStateService,
    private router: Router,
    private merakiAuthService: MerakiAuthService,
    private configService: ConfigService,
    private appInsightsService: ApplicationInsightsService,
    private analyticsService: PracticeAgentAnalyticsService,
    private widgetStateService: PracticeAgentWidgetStateService,
    private widgetControlService: PracticeAgentWidgetControlService,
    @Inject(WINDOW) private window: Window
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    if (this.widgetUrl && !this.widgetOrigin) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CONFIG_INVALID);
    }

    this.subscriptions.add(this.widgetControlService.openRequested$.pipe(tap(() => this.handleManualOpen())).subscribe());

    this.subscriptions.add(this.visible$.pipe(tap(visible => this.widgetControlService.setVisible(visible))).subscribe());

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? this.widgetStateService.state$ : EMPTY)),
          tap(state => this.handleWidgetState(state))
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.authService.authenticated$
        .pipe(
          distinctUntilChanged(),
          pairwise(),
          filter(([wasAuthenticated, authenticated]) => wasAuthenticated && !authenticated),
          tap(() => this.handleLogout())
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled =>
            enabled
              ? this.contextMessageRequests.pipe(
                  switchMap(request =>
                    this.merakiAuthService.getFirebaseToken().pipe(
                      catchError(() => of(null)),
                      map(firebaseIdToken => ({ request, firebaseIdToken }))
                    )
                  )
                )
              : EMPTY
          ),
          tap(({ request, firebaseIdToken }) => this.sendContextMessage(request, firebaseIdToken))
        )
        .subscribe()
    );

    this.subscriptions.add(this.enabled$.pipe(tap(enabled => this.handleEnabledChange(enabled))).subscribe());

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? this.context$ : EMPTY)),
          tap(context => this.handleContextChange(context))
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? fromEvent<MessageEvent>(this.window, 'message') : EMPTY)),
          tap(event => this.handleMessage(event))
        )
        .subscribe()
    );
  }

  connectIframe(iframe: HTMLIFrameElement): void {
    if (!this.enabled) {
      return;
    }

    const wasVisible = this.isEffectivelyVisible();
    this.stopAuthRefresh();
    this.iframe = iframe;
    this.iframeLoaded = true;
    this.initialContextSent = false;
    this.currentContextId = 0;
    this.paRequestedVisibilitySubject.next(wasVisible);
    this.expandedSubject.next(false);

    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_IFRAME_LOADED);
    this.startAuthRefresh();

    if (this.latestContext) {
      this.requestContextMessage('hbc.init', this.latestContext);
    }
  }

  handleIframeError(): void {
    this.hideWidget('iframe-load-failed');
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_IFRAME_LOAD_FAILED);
    this.stopAuthRefresh();
    this.stopContextRetry();
    this.iframe = null;
    this.iframeLoaded = false;
    this.initialContextSent = false;
  }

  dismiss(): void {
    if (!this.enabled || this.suppressedSubject.value) {
      return;
    }

    this.hideWidget('dismissed');
    this.clearPresentationAuthorization();
    this.stopSuppressionTimer();
    this.suppressionEndPending = false;
    this.suppressionMode = 'dismissed';
    this.suppressionUntil = this.getNextCalendarDayStart(this.getCurrentTime()).toISOString();
    this.suppressedSubject.next(true);
    this.saveSuppression();
    this.postMessage(this.createSuppressionMessage());
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_DISMISSED);
    this.trackAnalytics('widget-closed', {
      SessionEndReason: 'doctor_close',
      SuppressionUntil: this.suppressionUntil,
    });
    this.resumeSuppressionTimer();
  }

  snooze(): void {
    if (!this.enabled) {
      return;
    }

    this.hideWidget('snoozed');
    this.clearPresentationAuthorization();
    this.stopSuppressionTimer();
    this.suppressionEndPending = false;
    this.suppressionMode = 'snoozed';
    this.suppressionUntil = this.getSnoozeUntil(this.getCurrentTime()).toISOString();
    this.suppressedSubject.next(true);
    this.saveSuppression();
    this.postMessage(this.createSuppressionMessage());
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SNOOZED);
    this.trackAnalytics('widget-snoozed', {
      SessionEndReason: 'doctor_snooze',
      SnoozeDurationMs: this.snoozeDurationMs,
      SuppressionUntil: this.suppressionUntil,
    });

    this.resumeSuppressionTimer();
  }

  destroy(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.widgetControlService.setVisible(false);
    this.disconnectIframe(false);
    this.subscriptions.unsubscribe();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private handleEnabledChange(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.disconnectIframe(false);
      return;
    }

    this.startTimedRetriggerTimer();
    this.resumeSuppressionTimer();
  }

  trackCollapsed(collapsed: boolean): void {
    this.trackAnalytics(collapsed ? 'widget-collapsed' : 'widget-expanded');
  }

  private handleContextChange(context: PracticeAgentContext): void {
    if (context.currentPage !== 'home') {
      this.hideWidget('context-changed');
      this.clearPresentationAuthorization();
    }
    const homePageActive = context.currentPage === 'home';
    this.homePageActiveSubject.next(homePageActive);
    this.widgetControlService.setHomePageActive(homePageActive);
    this.latestContext = context;
    if (!this.iframeLoaded) {
      return;
    }

    const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
    this.requestContextMessage(messageType, context);
  }

  private requestContextMessage(type: ContextMessageType, context: PracticeAgentContext, sendSuppressionEnded = false): void {
    this.stopContextRetry();
    this.contextMessageRequests.next(this.createContextMessageRequest(type, context, sendSuppressionEnded));
  }

  private createContextMessageRequest(
    type: ContextMessageType,
    context: PracticeAgentContext,
    sendSuppressionEnded = false
  ): ContextMessageRequest {
    const contextId = ++this.nextContextId;
    return {
      type,
      context,
      sendSuppressionEnded: sendSuppressionEnded || this.suppressionEndPending,
      contextId,
      presentationMode: this.getPresentationMode(),
    };
  }

  private sendContextMessage(request: ContextMessageRequest, firebaseIdToken: string): void {
    if (!this.canPostMessage() || request.contextId !== this.nextContextId) {
      return;
    }

    if (!firebaseIdToken) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_AUTH_FAILED);
      if (request.sendSuppressionEnded) {
        this.scheduleSuppressionRetry();
      } else {
        this.scheduleContextRetry(request);
      }
      return;
    }

    this.stopContextRetry();

    if (request.sendSuppressionEnded) {
      this.suppressionMode = null;
      this.suppressionUntil = null;
      this.suppressionEndPending = false;
      this.suppressedSubject.next(false);
      this.clearSuppressionState();
    }

    this.postMessage(this.createAuthMessage(firebaseIdToken));

    if (request.sendSuppressionEnded) {
      this.postMessage(this.createSuppressionMessage());
    }

    const payload: PracticeAgentContextPayload = {
      ...request.context,
      contextId: request.contextId,
      presentationMode: request.presentationMode,
      suppression: this.getSuppression(),
    };

    const message: HbcInitMessage | HbcContextChangedMessage = {
      type: request.type,
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload,
    };

    this.postMessage(message);
    this.currentContextId = request.contextId;
    if (
      request.presentationMode === 'refresh' &&
      this.restoreVisibleAfterRefresh &&
      request.context.currentPage === 'home' &&
      !this.suppressedSubject.value
    ) {
      this.paRequestedVisibilitySubject.next(true);
      this.restoreVisibleAfterRefresh = false;
      this.startPresentationLeaseRenewal();
    }
    if (request.sendSuppressionEnded) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SUPPRESSION_ENDED);
    }
    this.initialContextSent = true;
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CONTEXT_SENT, {
      messageType: request.type,
      currentPage: request.context.currentPage,
    });
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.iframe || event.source !== this.iframe.contentWindow) {
      return;
    }

    if (event.origin !== this.widgetOrigin) {
      this.trackInvalidMessage('origin');
      return;
    }

    let message: PaVisibilityChangedMessage | PaSizeChangedMessage;
    if (isPaVisibilityChangedMessage(event.data)) {
      message = event.data;
    } else if (isPaSizeChangedMessage(event.data)) {
      message = event.data;
    } else {
      this.trackInvalidMessage('payload');
      return;
    }

    if (message.payload.contextId !== this.currentContextId || this.currentContextId !== this.nextContextId) {
      this.trackInvalidMessage('stale-context');
      return;
    }

    if (message.type === 'pa.size.changed') {
      if (message.payload.expanded && !this.isEffectivelyVisible()) {
        return;
      }

      this.expandedSubject.next(message.payload.expanded);
      return;
    }

    const { visible, reason } = message.payload;
    if (reason === 'card-api-failed') {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CARD_API_FAILED);
    }

    if (visible) {
      if (this.suppressedSubject.value || !this.homePageActiveSubject.value) {
        return;
      }

      const wasVisible = this.isEffectivelyVisible();
      if (!wasVisible && !this.restoreVisibleAfterRefresh && !this.presentationTrigger) {
        return;
      }

      if (this.restoreVisibleAfterRefresh) {
        this.paRequestedVisibilitySubject.next(true);
        this.restoreVisibleAfterRefresh = false;
        this.startPresentationLeaseRenewal();
        return;
      }

      if (wasVisible) {
        this.presentationTrigger = null;
        return;
      }

      if (this.presentationClaimPending) {
        return;
      }

      const presentationTrigger = this.presentationTrigger;
      const contextId = message.payload.contextId;
      this.presentationClaimPending = true;
      this.subscriptions.add(
        this.widgetStateService
          .claimPresentation(this.sessionId)
          .pipe(take(1))
          .subscribe(claimResult => {
            this.presentationClaimPending = false;
            const claimed = claimResult === 'claimed';
            const contextIsCurrent = contextId === this.currentContextId && this.currentContextId === this.nextContextId;
            const presentationIsCurrent = this.presentationTrigger === presentationTrigger;

            if (!claimed || !contextIsCurrent || !presentationIsCurrent || this.suppressedSubject.value) {
              if (!claimed && presentationIsCurrent && presentationTrigger === 'manual-open') {
                this.widgetControlService.setOpenResult(claimResult === 'blocked' ? 'already-open-elsewhere' : 'temporarily-unavailable');
              }
              this.clearPresentationAuthorization();
              if (claimed) {
                this.releasePresentationLease();
              }
              return;
            }

            this.paRequestedVisibilitySubject.next(true);
            this.presentationTrigger = null;
            this.startPresentationLeaseRenewal();
            this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CARD_AVAILABLE);
            this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SHOWN);
            this.trackAnalytics('widget-shown');
          })
      );
      return;
    }

    if (this.presentationTrigger === 'manual-open' && (reason === 'no-relevant-card' || reason === 'card-api-failed')) {
      this.widgetControlService.setOpenResult(reason);
    }
    this.clearPresentationAuthorization();
    this.hideWidget(reason);
  }

  private startAuthRefresh(): void {
    this.stopAuthRefresh();
    this.authRefreshSubscription = timer(AUTH_REFRESH_INTERVAL_MS, AUTH_REFRESH_INTERVAL_MS)
      .pipe(
        switchMap(() => this.merakiAuthService.getFirebaseToken().pipe(catchError(() => of(null)))),
        tap(firebaseIdToken => {
          if (firebaseIdToken && this.canPostMessage()) {
            if (this.latestContext && (this.currentContextId === 0 || this.currentContextId !== this.nextContextId)) {
              const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
              const request = this.createContextMessageRequest(messageType, this.latestContext);
              this.sendContextMessage(request, firebaseIdToken);
            } else {
              this.postMessage(this.createAuthMessage(firebaseIdToken));
            }
          } else if (this.canPostMessage()) {
            this.restoreVisibleAfterRefresh = this.isEffectivelyVisible();
            this.presentationTrigger = null;
            this.invalidateCurrentContext('auth-refresh-failed');
            this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_AUTH_FAILED);
            this.scheduleLatestContextRetry();
          }
        })
      )
      .subscribe();
  }

  private stopAuthRefresh(): void {
    if (this.authRefreshSubscription) {
      this.authRefreshSubscription.unsubscribe();
      this.authRefreshSubscription = null;
    }
  }

  private endSuppression(): void {
    if (!this.suppressionMode) {
      return;
    }

    const suppressionMode = this.suppressionMode;
    this.paRequestedVisibilitySubject.next(false);
    this.stopSuppressionTimer();

    if (suppressionMode === 'snoozed') {
      this.authorizePresentation('snooze-expiry');
    }

    if (this.iframeLoaded && this.latestContext) {
      this.suppressionEndPending = true;
      this.requestContextMessage('hbc.context.changed', this.latestContext, true);
      return;
    }

    this.suppressionMode = null;
    this.suppressionUntil = null;
    this.suppressionEndPending = false;
    this.suppressedSubject.next(false);
    this.clearSuppressionState();
  }

  private stopSuppressionTimer(): void {
    if (this.suppressionTimerSubscription) {
      this.suppressionTimerSubscription.unsubscribe();
      this.suppressionTimerSubscription = null;
    }
  }

  private resumeSuppressionTimer(): void {
    if (!this.suppressionMode || !this.suppressionUntil) {
      return;
    }

    const now = this.getCurrentTime();
    const suppressionEndMs = new Date(this.suppressionUntil).getTime();
    const remainingDurationMs = suppressionEndMs - now.getTime();
    if (remainingDurationMs <= 0) {
      this.endSuppression();
      return;
    }

    this.stopSuppressionTimer();
    if (this.suppressionMode === 'snoozed' && suppressionEndMs === this.getNextTimedRetrigger(now).getTime()) {
      return;
    }

    this.suppressionTimerSubscription = timer(remainingDurationMs)
      .pipe(tap(() => this.endSuppression()))
      .subscribe();
  }

  private startTimedRetriggerTimer(): void {
    this.stopTimedRetriggerTimer();
    const now = this.getCurrentTime();
    const delayMs = this.getNextTimedRetrigger(now).getTime() - now.getTime();
    this.timedRetriggerSubscription = timer(delayMs)
      .pipe(tap(() => this.handleTimedRetrigger()))
      .subscribe();
  }

  private handleTimedRetrigger(): void {
    this.startTimedRetriggerTimer();
    if (!this.enabled || this.suppressionMode === 'dismissed') {
      return;
    }

    if (this.suppressionMode === 'snoozed') {
      this.trackAnalytics('widget-daily-retriggered');
      this.endSuppression();
      return;
    }

    if (!this.iframeLoaded || !this.latestContext || !this.homePageActiveSubject.value || this.isEffectivelyVisible()) {
      return;
    }

    this.trackAnalytics('widget-daily-retriggered');
    this.authorizePresentation('home-after-16');
    this.requestContextMessage('hbc.context.changed', this.latestContext);
  }

  private stopTimedRetriggerTimer(): void {
    if (this.timedRetriggerSubscription) {
      this.timedRetriggerSubscription.unsubscribe();
      this.timedRetriggerSubscription = null;
    }
  }

  private scheduleSuppressionRetry(): void {
    this.stopSuppressionTimer();
    this.suppressionTimerSubscription = timer(SUPPRESSION_RETRY_INTERVAL_MS)
      .pipe(tap(() => this.endSuppression()))
      .subscribe();
  }

  private scheduleContextRetry(request: ContextMessageRequest): void {
    this.stopContextRetry();
    this.contextRetrySubscription = timer(CONTEXT_RETRY_INTERVAL_MS)
      .pipe(
        filter(() => request.contextId === this.nextContextId && this.canPostMessage()),
        tap(() => this.contextMessageRequests.next(request))
      )
      .subscribe();
  }

  private scheduleLatestContextRetry(): void {
    if (!this.latestContext) {
      return;
    }

    const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
    const request = this.createContextMessageRequest(messageType, this.latestContext);
    this.scheduleContextRetry(request);
  }

  private stopContextRetry(): void {
    if (this.contextRetrySubscription) {
      this.contextRetrySubscription.unsubscribe();
      this.contextRetrySubscription = null;
    }
  }

  private disconnectIframe(clearSuppression: boolean): void {
    this.hideWidget('disabled');
    this.stopAuthRefresh();
    this.stopSuppressionTimer();
    this.stopContextRetry();
    this.stopTimedRetriggerTimer();
    this.stopPresentationLeaseRenewal();
    this.iframe = null;
    this.iframeLoaded = false;
    this.initialContextSent = false;
    this.currentContextId = 0;
    this.nextContextId = 0;
    this.latestContext = null;
    this.suppressionEndPending = false;
    this.currentPage = null;
    this.currentHomeTab = 'other';
    this.clearPresentationAuthorization();
    this.homePageActiveSubject.next(false);
    this.widgetControlService.setHomePageActive(false);
    this.expandedSubject.next(false);

    if (clearSuppression) {
      this.suppressionMode = null;
      this.suppressionUntil = null;
      this.suppressedSubject.next(false);
    }
  }

  private handleLogout(): void {
    this.disconnectIframe(true);
  }

  private saveSuppression(): void {
    if (!this.suppressionMode || !this.suppressionUntil) {
      return;
    }

    this.subscriptions.add(this.widgetStateService.saveSuppression(this.suppressionMode, this.suppressionUntil).subscribe());
  }

  private clearSuppressionState(): void {
    this.subscriptions.add(this.widgetStateService.clearSuppression().subscribe());
  }

  private handleWidgetState(state: PracticeAgentWidgetState): void {
    const suppressionUntilMs = new Date(state.suppressionUntil || '').getTime();
    if (state.suppressionMode && Number.isFinite(suppressionUntilMs) && suppressionUntilMs > this.getCurrentTime().getTime()) {
      const suppressionChanged = this.suppressionMode !== state.suppressionMode || this.suppressionUntil !== state.suppressionUntil;
      this.hideWidget('shared-suppression');
      this.suppressionMode = state.suppressionMode;
      this.suppressionUntil = state.suppressionUntil;
      this.suppressionEndPending = false;
      this.suppressedSubject.next(true);
      this.clearPresentationAuthorization();
      this.resumeSuppressionTimer();
      if (suppressionChanged) {
        this.postMessage(this.createSuppressionMessage());
      }
      return;
    }

    if (!this.suppressionMode || this.suppressionEndPending) {
      return;
    }

    this.stopSuppressionTimer();
    this.suppressionMode = null;
    this.suppressionUntil = null;
    this.suppressedSubject.next(false);
    this.postMessage(this.createSuppressionMessage());
  }

  private getNextCalendarDayStart(now: Date): Date {
    const sastNow = new Date(now.getTime() + SAST_UTC_OFFSET_MS);
    const nextSastDayStartMs = Date.UTC(sastNow.getUTCFullYear(), sastNow.getUTCMonth(), sastNow.getUTCDate() + 1) - SAST_UTC_OFFSET_MS;
    return new Date(nextSastDayStartMs);
  }

  private getSnoozeUntil(now: Date): Date {
    const normalSnoozeEndMs = now.getTime() + this.snoozeDurationMs;
    const timedRetriggerMs = this.getNextTimedRetrigger(now).getTime();
    const finalHourStartMs = timedRetriggerMs - TIMED_RETRIGGER_SNOOZE_WINDOW_MS;
    return new Date(normalSnoozeEndMs >= finalHourStartMs ? timedRetriggerMs : normalSnoozeEndMs);
  }

  private getNextTimedRetrigger(now: Date): Date {
    const sastNow = new Date(now.getTime() + SAST_UTC_OFFSET_MS);
    let retriggerMs =
      Date.UTC(sastNow.getUTCFullYear(), sastNow.getUTCMonth(), sastNow.getUTCDate(), TIMED_RETRIGGER_HOUR_SAST) - SAST_UTC_OFFSET_MS;

    if (retriggerMs <= now.getTime()) {
      retriggerMs =
        Date.UTC(sastNow.getUTCFullYear(), sastNow.getUTCMonth(), sastNow.getUTCDate() + 1, TIMED_RETRIGGER_HOUR_SAST) - SAST_UTC_OFFSET_MS;
    }

    return new Date(retriggerMs);
  }

  private getCurrentTime(): Date {
    return new Date();
  }

  private trackAnalytics(eventType: PracticeAgentAnalyticsEventType, details: PracticeAgentAnalyticsDetails = {}): void {
    const analyticsDetails: PracticeAgentAnalyticsDetails =
      this.latestContext?.currentPage === 'home' ? { ...details, HomeTab: this.currentHomeTab } : details;
    this.subscriptions.add(
      this.analyticsService.track(eventType, this.latestContext?.currentPage || 'other', analyticsDetails).subscribe()
    );
  }

  private hideWidget(reason: string, sessionEndReason?: PracticeAgentSessionEndReason): void {
    const wasVisible = this.isEffectivelyVisible();
    this.paRequestedVisibilitySubject.next(false);
    this.expandedSubject.next(false);
    this.stopPresentationLeaseRenewal();
    if (wasVisible) {
      this.releasePresentationLease();
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_HIDDEN, { reason });
      if (sessionEndReason) {
        this.trackAnalytics('widget-closed', { SessionEndReason: sessionEndReason });
      }
    }
  }

  private authorizePresentation(trigger: PresentationTrigger): void {
    if (this.isEffectivelyVisible()) {
      return;
    }

    this.presentationTrigger = trigger;
  }

  private handleManualOpen(): void {
    if (!this.enabled || !this.iframeLoaded || this.latestContext?.currentPage !== 'home' || this.isEffectivelyVisible()) {
      return;
    }

    if (this.suppressedSubject.value) {
      this.stopSuppressionTimer();
      this.suppressionMode = null;
      this.suppressionUntil = null;
      this.suppressionEndPending = false;
      this.suppressedSubject.next(false);
      this.clearSuppressionState();
      this.postMessage(this.createSuppressionMessage());
    }

    this.authorizePresentation('manual-open');
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_MANUAL_OPEN_REQUESTED);
    this.requestContextMessage(this.initialContextSent ? 'hbc.context.changed' : 'hbc.init', this.latestContext);
  }

  private clearPresentationAuthorization(): void {
    this.presentationTrigger = null;
    this.restoreVisibleAfterRefresh = false;
  }

  private getPresentationMode(): PracticeAgentPresentationMode {
    if (this.presentationTrigger) {
      return 'present';
    }

    if (this.restoreVisibleAfterRefresh || this.isEffectivelyVisible()) {
      return 'refresh';
    }

    return 'hidden';
  }

  private handleNavigation(url: string): void {
    const nextPage = this.getCurrentPage(url);
    const nextHomeTab = this.getCurrentHomeTab(url);
    const enteredHome = nextPage === 'home' && this.currentPage !== 'home';
    const enteredPathologyCentre = nextPage === 'home' && nextHomeTab === 'path' && this.currentHomeTab !== 'path';
    const refreshPathologyContext = enteredPathologyCentre && this.currentPage === 'home';

    if (nextPage !== 'home') {
      this.hideWidget('context-changed', 'navigate_away');
      this.clearPresentationAuthorization();
    }

    this.currentPage = nextPage;
    this.currentHomeTab = nextHomeTab;

    if (!this.suppressedSubject.value) {
      if (enteredPathologyCentre) {
        this.authorizePresentation('pathology-centre');
      } else if (enteredHome) {
        this.authorizePresentation('home-landing');
      }
    }

    if (refreshPathologyContext && this.iframeLoaded && this.latestContext) {
      this.requestContextMessage('hbc.context.changed', this.latestContext);
    }
  }

  private invalidateCurrentContext(reason: string): void {
    const wasVisible = this.isEffectivelyVisible();
    this.paRequestedVisibilitySubject.next(false);
    this.expandedSubject.next(false);
    if (wasVisible) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_HIDDEN, { reason });
    }
    this.currentContextId = 0;
    this.nextContextId++;
  }

  private startPresentationLeaseRenewal(): void {
    this.stopPresentationLeaseRenewal();
    this.presentationLeaseSubscription = timer(PRESENTATION_LEASE_RENEWAL_INTERVAL_MS, PRESENTATION_LEASE_RENEWAL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.widgetStateService.claimPresentation(this.sessionId)),
        tap(claimResult => {
          if (claimResult === 'blocked') {
            this.hideWidget('presentation-lease-lost');
            this.clearPresentationAuthorization();
          }
        })
      )
      .subscribe();
  }

  private stopPresentationLeaseRenewal(): void {
    if (this.presentationLeaseSubscription) {
      this.presentationLeaseSubscription.unsubscribe();
      this.presentationLeaseSubscription = null;
    }
  }

  private releasePresentationLease(): void {
    this.subscriptions.add(this.widgetStateService.releasePresentation(this.sessionId).subscribe());
  }

  private getPresentationSessionId(): string {
    try {
      const storedSessionId = this.window.sessionStorage.getItem(PRESENTATION_SESSION_STORAGE_KEY);
      const isReload = this.getNavigationType() !== 'navigate';
      if (storedSessionId && this.isUuid(storedSessionId) && isReload) {
        return storedSessionId;
      }

      const sessionId = uuidV4();
      this.window.sessionStorage.setItem(PRESENTATION_SESSION_STORAGE_KEY, sessionId);
      return sessionId;
    } catch {
      return uuidV4();
    }
  }

  private getNavigationType(): string {
    const navigationEntry = this.window.performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry?.type) {
      return navigationEntry.type;
    }

    return this.window.performance?.navigation?.type === 1 ? 'reload' : 'navigate';
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private isEffectivelyVisible(): boolean {
    return this.paRequestedVisibilitySubject.value && this.homePageActiveSubject.value && !this.suppressedSubject.value && this.enabled;
  }

  private createAuthMessage(firebaseIdToken: string): HbcAuthChangedMessage {
    return {
      type: 'hbc.auth.changed',
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload: { firebaseIdToken },
    };
  }

  private createSuppressionMessage(): HbcSuppressionChangedMessage {
    return {
      type: 'hbc.suppression.changed',
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload: this.getSuppression(),
    };
  }

  private getSuppression(): PracticeAgentSuppression {
    if (!this.suppressedSubject.value) {
      return { suppressed: false };
    }

    return {
      suppressed: true,
      mode: this.suppressionMode,
      ...(this.suppressionUntil ? { until: this.suppressionUntil } : {}),
    };
  }

  private postMessage(message: HbcPracticeAgentMessage): void {
    if (this.canPostMessage()) {
      this.iframe.contentWindow.postMessage(message, this.widgetOrigin);
    }
  }

  private canPostMessage(): boolean {
    return this.enabled && this.iframeLoaded && !!this.iframe?.contentWindow && !!this.widgetOrigin;
  }

  private trackInvalidMessage(reason: string): void {
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_INVALID_MESSAGE_REJECTED, { reason });
  }

  private trackEvent(event: AppInsightTags, properties: { [name: string]: string } = {}): void {
    this.appInsightsService.trackEvent(event, {
      ...properties,
      contractVersion: PRACTICE_AGENT_MESSAGE_VERSION.toString(),
    });
  }

  private getWidgetOrigin(widgetUrl: string): string | null {
    if (!widgetUrl) {
      return null;
    }

    try {
      const url = new URL(widgetUrl);
      const isDifferentOrigin = url.origin !== this.window.location.origin;
      const isLocalHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname);

      return isDifferentOrigin && (url.protocol === 'https:' || (!environment.production && isLocalHttp)) ? url.origin : null;
    } catch {
      return null;
    }
  }

  private getSnoozeDurationMs(): number {
    const configuredDuration = this.configService.config.practiceAgent.snoozeDurationMs;
    return Number.isFinite(configuredDuration) && configuredDuration > 0 ? configuredDuration : SNOOZE_DURATION_MS;
  }

  private getSnoozeDurationLabel(): string {
    const hours = this.snoozeDurationMs / SNOOZE_DURATION_MS;
    if (Number.isInteger(hours)) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return `${this.snoozeDurationMs / 1000} seconds`;
  }

  get validatedWidgetUrl(): string | null {
    return this.widgetOrigin ? this.widgetUrl : null;
  }

  private getContextPatientId(
    currentPage: PracticeAgentPage,
    currentPatientId: string,
    activePatients: Array<{ patientId: string }>
  ): string | null {
    const isPatientPage = ['patient-summary', 'patient-consultation', 'patient-record'].includes(currentPage);
    if (!isPatientPage || !currentPatientId) {
      return null;
    }

    return activePatients?.some(patient => patient.patientId === currentPatientId) ? currentPatientId : null;
  }

  private getCurrentPage(url: string): PracticeAgentPage {
    const path = (url || '').split(/[?#]/)[0];
    const segments = path.split('/').filter(segment => !!segment);
    const area = segments[1];

    if (area === 'home') {
      return 'home';
    }

    if (area === 'provider') {
      return 'provider';
    }

    if (area !== 'patient') {
      return 'other';
    }

    const patientSegment = segments[2];
    const patientPage = segments[3];
    if (!patientSegment || ['add', 'search', 'mobile-search'].includes(patientSegment)) {
      return 'patient-search';
    }

    if (patientPage === 'dashboard') {
      return 'patient-summary';
    }

    if (patientPage === 'consult') {
      return 'patient-consultation';
    }

    return 'patient-record';
  }

  private getCurrentHomeTab(url: string): PracticeAgentHomeTab {
    if (this.getCurrentPage(url) !== 'home') {
      return 'other';
    }

    try {
      const homeTab = new URL(url, this.window.location.origin).searchParams.get('tab') || 'schedule';
      const allowedTabs: PracticeAgentHomeTab[] = ['schedule', 'tracker', 'history', 'path', 'documents', 'whatsapp'];
      return allowedTabs.includes(homeTab as PracticeAgentHomeTab) ? (homeTab as PracticeAgentHomeTab) : 'other';
    } catch {
      return 'other';
    }
  }
}
